import type { ExerciseSet } from "../interfaces/FinishWorkoutStats";

const parseExerciseDetailNumber = (value: unknown): number | null => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsedValue = +value.replace(/\D/g, "");
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  return null;
};

const buildExerciseSet = ({
  reps,
  rest,
  weight,
  exerciseId,
  parentExerciseId,
  workoutId,
}: ExerciseSet): ExerciseSet => ({
  reps,
  rest,
  weight,
  exerciseId,
  parentExerciseId,
  workoutId,
});

const normalizeExerciseSets = (
  sets: ExerciseSet[],
  exercise,
  workoutId
): ExerciseSet[] => {
  const expectedSetCount = parseExerciseDetailNumber(exercise.details?.sets);

  if (!expectedSetCount || sets.length >= expectedSetCount) {
    return sets;
  }

  const fallbackReps =
    parseExerciseDetailNumber(exercise.details?.reps) ??
    sets[sets.length - 1]?.reps ??
    10;
  const fallbackRest =
    parseExerciseDetailNumber(exercise.details?.rest) ??
    sets[sets.length - 1]?.rest ??
    2;
  const fallbackWeight = sets[sets.length - 1]?.weight ?? null;
  const normalizedSets = [...sets];

  while (normalizedSets.length < expectedSetCount) {
    normalizedSets.push(
      buildExerciseSet({
        reps: fallbackReps,
        rest: fallbackRest,
        weight: fallbackWeight,
        exerciseId: exercise.id,
        parentExerciseId: exercise.parentExerciseId,
        workoutId,
      })
    );
  }

  return normalizedSets;
};

const generateFinishExerciseArray = (
  exercise,
  workoutId,
  exerciseStats = exercise.stats
) => {
  const s: ExerciseSet[] = [];
  const exerciseId = exercise.id;
  const resolvedWorkoutId = workoutId ?? exercise.workoutId;
  const sourceStats = Array.isArray(exerciseStats) ? exerciseStats : [];

  if (sourceStats.length > 0) {
    sourceStats.forEach((row) => {
      s.push(
        buildExerciseSet({
          reps: row.reps,
          rest: row.rest,
          weight: row.weight,
          exerciseId,
          parentExerciseId: row.parentExerciseId ?? exercise.parentExerciseId,
          workoutId: row.workoutId ?? resolvedWorkoutId,
        })
      );
    });

    return normalizeExerciseSets(s, exercise, resolvedWorkoutId);
  }

  const expectedSetCount = parseExerciseDetailNumber(exercise.details?.sets);

  if (expectedSetCount) {
    const reps = parseExerciseDetailNumber(exercise.details?.reps);
    const rest = parseExerciseDetailNumber(exercise.details?.rest);

    Array.from({ length: expectedSetCount }).forEach(() => {
      s.push(
        buildExerciseSet({
          reps: reps ?? 10,
          rest: rest ?? 2,
          weight: null,
          exerciseId,
          workoutId: resolvedWorkoutId,
          parentExerciseId: exercise.parentExerciseId,
        })
      );
    });
  } else {
    for (let i = 0; i < 3; i++) {
      s.push(
        buildExerciseSet({
          reps: 10,
          rest: 2,
          weight: 0,
          exerciseId,
          parentExerciseId: exercise.parentExerciseId,
          workoutId: resolvedWorkoutId,
        })
      );
    }
  }

  return s;
};

export { generateFinishExerciseArray };
